import styled from 'styled-components';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/Button';
import { Typography } from '@mui/material';
import { Icon } from '@components/icon';

export const Container = styled(Box).attrs({ component: 'main' })`
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(420px, 5fr);
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  position: relative;
  isolation: isolate;
  background-color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? theme.palette.background.default : '#ffffff'};

  @media (max-width: 899px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto 1fr;
  }
`;

export const Square = styled(Box).attrs({
  width: 20,
  bgcolor: 'white.dark',
  flex: 1,
  flexDirection: 'column',
})``;

export const LogoWrapper = styled(Box).attrs({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  bottom: 0,
  right: 0,
  mb: 5,
  mr: 8,
})``;

export const Wrapper = styled(Box).attrs({
  position: 'absolute',
  bottom: 0,
  right: 0,
})``;

export const LeftContent = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-width: 0;
  padding: 48px 12%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.gradients.secondary};
  overflow: hidden;

  @media (max-width: 899px) {
    min-height: 140px;
    padding: 24px;
  }
`;

export const RightContent = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  position: relative;
  box-sizing: border-box;
  padding: 40px clamp(24px, 4vw, 80px);

  @media (max-width: 899px) {
    padding: 32px 24px;
  }
`;

export const Form = styled(Box).attrs({
  display: 'flex',
  flexDirection: 'column',
})``;

export const InsightsIcon = styled(Icon).attrs({
  name: 'insights',
  alt: 'Insights Icone',
})<{ name: string; alt: string }>`
  width: 18rem;
`;

export const LogoIconDark = styled.img.attrs(({ theme }) => ({
  src: theme.media.logos.dark,
  alt: 'Logo Icone',
}))<{ src?: string; alt?: string }>`
  display: block;
  width: min(320px, 100%);
  height: auto;
  max-height: 160px;
  object-fit: contain;
  align-self: center;
  position: relative;
  z-index: 2;
`;

export const LogoIconLight = styled(LogoIconDark).attrs(({ theme }) => ({
  src: theme.media.logos.light,
}))``;

export const CircularEffect = styled(Box).attrs({ 'aria-hidden': true })`
  position: absolute;
  right: 12%;
  top: -10%;
  width: 42%;
  height: 120%;
  border-left: 24px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  pointer-events: none;

  @media (max-width: 899px) { display: none; }
`;

export const CircularEffectInside = styled(Box).attrs({ 'aria-hidden': true })`
  position: absolute;
  right: -1px;
  top: -10%;
  width: 24%;
  height: 120%;
  border-radius: 100% 0 0 100% / 50% 0 0 50%;
  background-color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? theme.palette.background.default : '#ffffff'};
  pointer-events: none;

  @media (max-width: 899px) { display: none; }
`;

export const UnionIcon = styled(Icon).attrs({
  name: 'union',
  alt: 'Union Icone',
})<{ name?: string; alt?: string }>`
  width: 15rem;
`;

export const Button = styled(ButtonBase).attrs({
  variant: 'contained',
  sx: {
    mt: 3,
    mb: 2,
    fontWeight: 700,
    fontSize: 16,
    py: 1.2,
    px: 6,
    borderRadius: 1.5,
    boxShadow: (theme: any) =>
      theme.palette.mode === 'dark'
        ? '0 4px 14px 0 rgba(0, 0, 0, 0.5)'
        : '0 4px 14px 0 rgba(19, 74, 85, 0.3)',
    '&:hover': {
      filter: 'brightness(1.1)',
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
})`
  background: ${({ theme }) => theme.gradients.primary};
  color: #ffffff;
  font-family: 'Mukta';
  transition: all 0.2s ease-in-out;
`;

export const ButtonWrapper = styled(Box).attrs({
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
})``;

export const UnderlineButton = styled(ButtonBase).attrs({
  sx: {
    textDecoration: 'underline',
    color: 'text.secondary',
    fontSize: 14,
    '&:hover': {
      color: 'text.primary',
    },
  },
})``;

export const ModalContainer = styled(Box).attrs({
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
})`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const ModalContent = styled(Box)`
  overflow: hidden;
  overflow-y: scroll;
  scrollbar-width: none;
  box-sizing: content-box;
  max-height: 500px;
  max-width: 700px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const Description = styled(Typography).attrs({
  variant: 'h6',
  fontFamily: 'Mukta',
  fontWeight: 400,
  color: 'text.secondary',
})``;

export const SignUpButton = styled(ButtonBase).attrs({
  variant: 'outlined',
  sx: {
    fontFamily: 'Mukta',
    fontSize: 18,
    fontWeight: 700,
    color: 'text.primary',
    border: '1px solid',
    borderColor: (theme: any) =>
      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.23)',
    px: 5,
    py: 1,
    borderRadius: 1.5,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: 'primary.main',
      color: 'primary.main',
      bgcolor: (theme: any) =>
        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
    },
  },
})``;
